import MapboxDraw, { DrawCustomMode, DrawCustomModeThis } from "@mapbox/mapbox-gl-draw";
import { Position } from "geojson";
import { defer } from "lodash";

import { DRAWING_CONSTANTS } from "fond/constants";
import { LayerIds } from "fond/types";

import drawPointMode from "../../base/drawPoint";
import { canPlacePoint } from "../../base/helper";

import { pullInFeatures, refreshSnappedStatus, splitPaths } from "./helper";

const { constants: Constants, modes } = MapboxDraw;

/*
 * A modified draw_point mode that stays in draw_point when a point is created,
 * rather than moving back to simple_select and requiring the user to re-enter
 * draw point mode to create the next point.
 */
function addPoint(this: DrawCustomModeThis, coordinates: Position): MapboxDraw.DrawPoint {
  const point = this.newFeature({
    type: Constants.geojsonTypes.FEATURE,
    properties: { layerId: LayerIds.inPole, [DRAWING_CONSTANTS.Unsnapped]: "true" },
    geometry: {
      type: Constants.geojsonTypes.POINT,
      coordinates,
    },
  }) as MapboxDraw.DrawPoint;

  this.addFeature(point);

  return point;
}

/**
 * This is a custom mode for drawing poles in the aerial planner.
 * It extends the draw_point mode and adds functionality for snapping to spans.
 * It also handles the creation of poles and splitting paths when a pole is dropped on a span.
 */
const drawPole: DrawCustomMode = {
  ...drawPointMode,

  onClick: function (this, state, e) {
    if (!canPlacePoint(state, e)) return;

    const feature = addPoint.call(this, [state.snappedLng, state.snappedLat]);

    // Get collocated spans and poles
    const { spans, poles } = pullInFeatures.call(this, feature);

    if (poles.length > 0) {
      // If pole is created on top of another delete the newly created pole
      this.deleteFeature(feature.id.toString(), { silent: true });
      return;
    } else if (spans.length > 0) {
      // If pole is created on top of a span, we want to split the path into two segments
      // and create a new pole at the snapped location.
      // Multiple spans can exist at a T-intersection; we must attempt to split all of them.
      spans.forEach((span) => splitPaths.call(this, span, feature));
    }

    // We need to refresh the snapped status of the feature
    // to ensure that it is correctly marked as (un)snapped.
    refreshSnappedStatus.call(this, feature);

    // We defer the firing of this event to allow the splitPaths defaral to occur first
    defer(() => {
      this.map.fire("draw.create", { features: [feature.toGeoJSON()] });
    });
  },
};

export default drawPole;
